import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bot,
  CheckCircle2,
  Clock3,
  MessageSquareText,
  Send,

  Zap,
} from "lucide-react";

const features = [
  {
    icon: Send,
    title: "Bulk Message Dispatcher",
    description:
      "Send targeted promotional, transactional, and follow-up templates to custom contact groups with variable text tags.",
  },
  {
    icon: MessageSquareText,
    title: "Interactive Bot & Keywords",
    description:
      "Set automated responses when customers message keywords like #DEMO, #PRICING, #BROCHURE, or #SUPPORT 24/7.",
  },
  {
    icon: Clock3,
    title: "Smart Schedule Automation",
    description:
      "Queue campaigns to execute at optimal delivery windows. Includes delivery rate tracking and contact import/export.",
  },
];

function Software() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoaded(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <main className="min-h-screen overflow-hidden bg-[#f6f8fb] text-slate-900">
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-[#08111f] text-white">
        {/* Animated background */}
        <div className="pointer-events-none absolute -left-40 top-0 h-96 w-96 animate-[softwareFloat_8s_ease-in-out_infinite] rounded-full bg-emerald-500/10 blur-[120px]" />

        <div className="pointer-events-none absolute -right-40 bottom-0 h-[28rem] w-[28rem] animate-[softwareFloat_10s_ease-in-out_infinite_reverse] rounded-full bg-blue-500/10 blur-[130px]" />

        <div className="pointer-events-none absolute left-1/2 top-0 h-px w-1/2 -translate-x-1/2 bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" />

        <div
          className={`relative mx-auto max-w-7xl px-5 pb-16 pt-14 transition-all duration-1000 sm:px-6 sm:pb-20 sm:pt-18 lg:px-8 lg:pb-24 lg:pt-20 ${
            loaded
              ? "translate-y-0 opacity-100"
              : "translate-y-8 opacity-0"
          }`}
        >
          <div className="grid items-center gap-12 lg:grid-cols-[1.2fr_0.8fr]">
            {/* HERO CONTENT */}
            <div>
              {/* Refrex */}
              <div className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-emerald-400/20 bg-emerald-400/[0.08] px-4 py-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#00a96d] shadow-lg shadow-emerald-950/30">
                  <span className="text-xs font-black text-white">R</span>
                </div>

                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-300">
                  Referx
                </span>
              </div>

              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">
                WhatsApp Automation Software
              </p>

              <h1 className="mt-4 max-w-3xl text-4xl font-black leading-[1.05] tracking-[-0.055em] sm:text-5xl lg:text-6xl">
                WhatsApp Automation
                <span className="text-emerald-400"> Software</span>
              </h1>

              <p className="mt-6 max-w-2xl text-sm leading-7 text-slate-400 sm:text-base">
                Enterprise cloud automation utility designed for mass
                broadcasting, smart keyword bots, contact tagging, and
                campaign analytics.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/signup"
                  className="group inline-flex min-h-12 items-center justify-center rounded-xl bg-[#00a96d] px-6 py-3 text-sm font-black text-white shadow-xl shadow-emerald-950/30 transition-all duration-300 hover:-translate-y-1 hover:bg-[#00945f]"
                >
                  Buy Software Now (₹1,000)

                  <ArrowRight
                    size={16}
                    className="ml-2 transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>
              </div>
            </div>

            {/* PRICE CARD */}
            <div
              className={`relative transition-all delay-300 duration-1000 ${
                loaded
                  ? "translate-x-0 opacity-100"
                  : "translate-x-10 opacity-0"
              }`}
            >
              <div className="relative overflow-hidden rounded-3xl border border-white/[0.08] bg-white/[0.045] p-6 shadow-2xl backdrop-blur-xl sm:p-8">
                <div className="pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full bg-emerald-400/10 blur-[80px]" />

                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-400">
                      <Zap size={22} />
                    </div>

                    <span className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1.5 text-[9px] font-black uppercase tracking-wider text-emerald-300">
                      Lifetime
                    </span>
                  </div>

                  <div className="mt-8">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                      Software Access
                    </p>

                    <div className="mt-2 flex items-end gap-2">
                      <span className="text-5xl font-black tracking-[-0.06em] text-white">
                        ₹1,000
                      </span>

                      <span className="pb-2 text-xs font-semibold text-slate-500">
                        Monthly Subscription
                      </span>
                    </div>

                    <p className="mt-2 text-xs text-emerald-400">
                      Lifetime License
                    </p>
                  </div>

                  <div className="mt-8 space-y-3">
                    {[
                      "Cloud automation access",
                      "Campaign automation",
                      "Keyword-based bots",
                      "Campaign analytics",
                    ].map((item) => (
                      <div
                        key={item}
                        className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.025] px-3.5 py-3"
                      >
                        <CheckCircle2
                          size={15}
                          className="shrink-0 text-emerald-400"
                        />

                        <span className="text-xs font-semibold text-slate-300">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FEATURES ================= */}
      <section className="relative overflow-hidden bg-[#f6f8fb]">
        <div className="pointer-events-none absolute left-1/2 top-20 h-96 w-96 -translate-x-1/2 rounded-full bg-emerald-100/30 blur-[130px]" />

        <div className="relative mx-auto max-w-7xl px-5 py-14 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600">
              Software Features
            </p>

            <h2 className="mt-3 text-3xl font-black tracking-[-0.045em] text-[#08111f] sm:text-4xl">
              Built for WhatsApp Automation
            </h2>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {features.map((feature, index) => {
              const Icon = feature.icon;

              return (
                <div
                  key={feature.title}
                  className={`group relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_10px_35px_rgba(15,23,42,0.05)] transition-all duration-700 hover:-translate-y-2 hover:border-emerald-200 hover:shadow-[0_24px_60px_rgba(15,23,42,0.10)] ${
                    loaded
                      ? "translate-y-0 opacity-100"
                      : "translate-y-10 opacity-0"
                  }`}
                  style={{
                    transitionDelay: `${index * 140 + 250}ms`,
                  }}
                >
                  {/* Animated top line */}
                  <div className="absolute left-0 right-0 top-0 h-1 origin-left scale-x-0 bg-[#00a96d] transition-transform duration-500 group-hover:scale-x-100" />

                  <div className="p-6 sm:p-7">
                    <div className="flex items-center justify-between">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 group-hover:bg-emerald-100">
                        <Icon size={22} strokeWidth={1.8} />
                      </div>

                      <span className="text-5xl font-black tracking-[-0.08em] text-slate-100">
                        0{index + 1}
                      </span>
                    </div>

                    <h3 className="mt-7 text-xl font-black tracking-[-0.03em] text-[#08111f]">
                      {feature.title}
                    </h3>

                    <p className="mt-3 text-sm leading-7 text-slate-500">
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="relative overflow-hidden bg-[#08111f] text-white">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full bg-emerald-500/10 blur-[110px]" />

        <div className="relative mx-auto max-w-4xl px-5 py-14 text-center sm:px-6 sm:py-18 lg:px-8 lg:py-20">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-400">
            <Bot size={21} />
          </div>

          <h2 className="mt-5 text-3xl font-black tracking-[-0.045em] sm:text-4xl">
            Ready to automate your WhatsApp communication?
          </h2>

          <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-slate-400">
            Unlock software access immediately upon ₹1,000 payment
            confirmation. Qualify for assigned tasks and start referring
            peers.
          </p>

          <Link
            to="/signup"
            className="group mt-7 inline-flex min-h-12 items-center justify-center rounded-xl bg-[#00a96d] px-7 py-3 text-sm font-black text-white shadow-xl shadow-emerald-950/30 transition-all duration-300 hover:-translate-y-1 hover:bg-[#00945f]"
          >
            Buy Software Now (₹1,000)

            <ArrowRight
              size={16}
              className="ml-2 transition-transform duration-300 group-hover:translate-x-1"
            />
          </Link>
        </div>
      </section>

      {/* ================= ANIMATIONS ================= */}
      <style>{`
        @keyframes softwareFloat {
          0%,
          100% {
            transform: translate3d(0, 0, 0) scale(1);
          }

          50% {
            transform: translate3d(0, -18px, 0) scale(1.04);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </main>
  );
}

export default Software;